import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import AppNotifications from '../../../AppNotifications';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AppTooltip from '../../../AppTooltip';
import {
  isSuperAdmin,
  isActualSuperAdmin,
  isParent,
  isActualParent,
  isStudent,
  useRolePermissions,
  isAdmin,
  isActualAdmin,
} from '@projectK/context/RolesProvider';
import UserInfo from '../../components/UserInfo';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInterval } from '@projectK/hooks/useInterval';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import { alpha, Badge, Switch } from '@mui/material';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import { AiOutlineSwap } from 'react-icons/ai';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import IonicHeader from '../../HorDefault/IonicHeader';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import { useSwitchBack } from '@projectK/hooks/SwitchBackHelper';
import { useDashboard } from '@projectK/context/DashboardProvider';
import { useMissedPaymentSettings } from '@projectK/context/checkMissedPayments';
import GracePeriodBanner from '../GracePeriodBanner';

const isAdminRole = (userData, actualRole) => {
  const isAdminRole = isSuperAdmin(userData) || isAdmin(userData);
  const isActualAdminRole =
    isActualSuperAdmin(userData) || isActualAdmin(userData);

  return actualRole === false ? isAdminRole : isActualAdminRole || isAdminRole;
};

const isParentRole = (userData, actualRole) => {
  const isParentRole = isParent(userData);
  const isActualParentRole = isActualParent(userData);

  return actualRole === false
    ? isParentRole
    : isActualParentRole || isParentRole;
};

const isStudentRole = (userData) => {
  return isStudent(userData);
};

const AppHeader = (props) => {
  const { user } = useAuthUser();
  const { isMobile } = useMobileSettings();
  const { pathname } = useLocation();
  const [settings, setSettings] = useState(null);
  const { switchBack } = useSwitchBack();
  const { swapRoles, actualSwapRoles, roleData, userData, actualRoleData } =
    useRolePermissions();
  const { checkGracePeriodPayment } = useMissedPaymentSettings();
  const fetchSettings = async () => {
    try {
      const response = await jwtAxios.get(`/app-settings/`);
      setSettings(response.data);
    } catch (error) {
      // Handle error
      console.error('Error fetching appearance settings:', error);
      if (error.response.data.error === 'No Subdomain found') {
        window.location.href = `${process.env.NX_BASE_DOMAIN}/404`;
      }
    }
  };
  const hideHeader = pathname.includes(`/${user.roleMenuFlag[0]}/events`);
  useEffect(() => {
    if (!isMobile) {
      fetchSettings();
    }
  }, [isMobile]);
  const {
    isCollapsed,
    setCollapsed,
    toggleNavCollapsed,
    tooltipPosition,
    fetchDataFunction,
    commonRoutes,
  } = props;
  const navigate = useNavigate();
  const [roleAnchorEl, setRoleAnchorEl] = React.useState(null);
  const infoViewActionsContext = useInfoViewActionsContext();
  const { showDashboard, setShowDashboard } = useDashboard();
  const canSwapToIds = [
    ...new Set([
      ...(swapRoles?.[roleData?.id] || []),
      ...(actualSwapRoles?.[actualRoleData?.id] || []),
    ]),
  ];
  let companyTheme = CompanyTheme();
  useEffect(() => {
    if (!companyTheme) {
      //set them all to the hard coded values
      companyTheme = {
        primary: { main: '#3d3d3d' },
        secondary: { main: '#ccc' },
        black: '#3d3d3d',
        white: '#fff',
      };
      return;
    }
    // Set CSS variables
    document.documentElement.style.setProperty(
      '--company-primary',
      companyTheme ? companyTheme?.primary?.main : 'text.primary',
    );
    document.documentElement.style.setProperty(
      '--company-secondary',
      companyTheme ? companyTheme?.secondary?.main : '#27282a',
    );
    document.documentElement.style.setProperty(
      '--company-black',
      companyTheme?.black || '#000',
    );
    document.documentElement.style.setProperty(
      '--company-white',
      companyTheme?.white || '#fff',
    );
  }, [companyTheme]);

  useEffect(() => {
    if (fetchDataFunction) {
      getAllCartItems();
    }
  }, [fetchDataFunction]);

  const [notificationData, setNotificationData] = useState([]);
  const notifications = async () => {
    try {
      const response = await jwtAxios.get(`/notification`);
      const { data } = response;
      setNotificationData(data.list);
    } catch (error) {
      console.error('Error fetching notifications', error);
    }
  };

  const [cartData, setCartData] = useState([]);
  const getAllCartItems = async () => {
    try {
      const response = await jwtAxios.get(`/cart`);
      const { data } = response;
      setCartData(data);
    } catch (error) {
      console.error('Error fetching cart items', error);
    }
  };
  const handleRoleOpenMenu = (event) => {
    setRoleAnchorEl(event.currentTarget);
  };

  const handleRoleCloseMenu = () => {
    setRoleAnchorEl(null);
  };

  useInterval(() => {
    user && notifications();
    if (isStudent(user) || isParent(user)) {
      getAllCartItems();
    }
  }, 30000);

  /***********************Notification Mark all as Read********************* */

  const markAllRead = async (itemId) => {
    try {
      await jwtAxios.put(`/notification/user/${itemId}`);
      notifications();
    } catch (error) {
      infoViewActionsContext.fetchError(error.response.data.error);
    }
  };

  /***********************Switch settings********************* */

  const switchActualUserBack = async () => {
    try {
      // Make the POST request using jwtAxios
      await jwtAxios.post(`/admin/actual-user`);

      window.location.reload();
    } catch (error) {
      // Handle the error here
      console.error(error);
    }
  };

  const [isJSDLoaded, setIsJSDLoaded] = useState(false);

  useEffect(() => {
    const checkJSD = () => {
      let iframe = document.querySelector('#jsd-widget'); // Get the widget iframe
      if (iframe) {
        setIsJSDLoaded(true);
      } else {
        setTimeout(checkJSD, 500); // Keep checking every 500ms
      }
    };
    checkJSD();
  }, [isJSDLoaded]);

  const renderRoleSwitcher = () => {
    if (user?.actualUser) {
      return (
        <Box sx={{ px: 1.85 }}>
          <AppTooltip placement={tooltipPosition}>
            <IconButton
              className='icon-btn'
              sx={{
                borderRadius: '50%',
                width: 40,
                height: 40,
                color: (theme) => theme.palette.text.secondary,
                backgroundColor: (theme) => theme.palette.background.default,
                border: 1,
                borderColor: 'transparent',
                transition: 'transform 0.3s ease-in-out',
                '&:hover, &:focus': {
                  color: (theme) => theme.palette.text.primary,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.background.default, 0.9),
                  borderColor: (theme) =>
                    alpha(theme.palette.text.secondary, 0.25),
                  transform: 'scale(1.1)',
                },
              }}
              onClick={handleRoleOpenMenu}
              size='large'
            >
              <AiOutlineSwap />
            </IconButton>

            <Menu
              anchorEl={roleAnchorEl}
              open={Boolean(roleAnchorEl)}
              onClose={handleRoleCloseMenu}
            >
              {userData?.roles
                ?.filter((r) => canSwapToIds?.includes(r.id))
                .map((role) =>
                  user?.roleIds?.[0] !== role?.id ? (
                    <MenuItem
                      sx={{ textTransform: 'capitalize' }}
                      key={role.role_name}
                    >
                      <input
                        type='button'
                        value={`Swap to ${role?.alias_name || role?.role_name}`}
                        onClick={() => {
                          const actualRole =
                            user.actualRoleIds[0] || user?.roleIds[0];
                          switchBack({
                            id: user.id,
                            role: role.id,
                            actualRole: actualRole,
                            actualUser: user.actualUser,
                            parentuser: null,
                          });
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          font: 'inherit',
                          cursor: 'pointer',
                        }}
                      />
                    </MenuItem>
                  ) : null,
                )}

              {user?.parentuser && (
                <MenuItem
                  sx={{ textTransform: 'capitalize' }}
                  onClick={() =>
                    switchBack({
                      id: user.parentuser,
                      role: user?.parentuserRoleId,
                      actualRole: user?.actualRoleIds[0],
                      actualUser: user?.actualUser,
                      parentuser: null,
                    })
                  }
                >
                  Swap Back to {user?.parentuserRole}
                </MenuItem>
              )}

              <MenuItem
                sx={{ textTransform: 'capitalize' }}
                onClick={switchActualUserBack}
              >
                Swap Back
              </MenuItem>
            </Menu>
          </AppTooltip>
        </Box>
      );
    }

    if (userData?.roles?.length > 1) {
      return (
        <Box sx={{ px: 1.85 }}>
          <AppTooltip placement={tooltipPosition}>
            <IconButton
              className='icon-btn'
              sx={{
                borderRadius: '50%',
                width: 40,
                height: 40,
                color: (theme) => theme.palette.text.secondary,
                backgroundColor: (theme) => theme.palette.background.default,
                border: 1,
                borderColor: 'transparent',
                transition: 'transform 0.3s ease-in-out',
                '&:hover, &:focus': {
                  color: (theme) => theme.palette.text.primary,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.background.default, 0.9),
                  borderColor: (theme) =>
                    alpha(theme.palette.text.secondary, 0.25),
                  transform: 'scale(1.1)',
                },
              }}
              onClick={handleRoleOpenMenu}
              size='large'
            >
              <PublishedWithChangesIcon />
            </IconButton>

            <Menu
              anchorEl={roleAnchorEl}
              open={Boolean(roleAnchorEl)}
              onClose={handleRoleCloseMenu}
            >
              {userData?.roles?.map((role, index) =>
                userData?.roles?.[0]?.id !== role?.id ? (
                  <MenuItem
                    sx={{ textTransform: 'capitalize' }}
                    key={role?.alias_name || role?.role_name}
                    onClick={() =>
                      switchBack({
                        id: user.id,
                        role: userData?.roles?.[index]?.id,
                        actualRole:
                          user.actualRoleIds?.[0] ?? userData?.roles?.[0]?.id,
                        actualUser: user?.actualUser ?? user.id,
                      })
                    }
                  >
                    <>Swap to {role?.alias_name || role?.role_name}</>
                  </MenuItem>
                ) : null,
              )}
            </Menu>
          </AppTooltip>
        </Box>
      );
    }

    return null;
  };
  if (hideHeader) {
    return null;
  }
  return isMobile ? (
    <IonicHeader
      toggleNavCollapsed={toggleNavCollapsed}
      routesConfig={commonRoutes}
      socialSettings={settings?.social}
      companyData={settings?.basic}
      GracePeriodBanner={
        checkGracePeriodPayment.length > 0 ? (
          <GracePeriodBanner
            checkGracePeriodPayment={checkGracePeriodPayment}
            className={'mobile'}
          />
        ) : null
      }
    />
  ) : (
    <AppBar
      position='relative'
      color='inherit'
      sx={{
        boxShadow: 'none',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
        transition: 'width 0.5s ease',
        width: '100%',
      }}
      className='app-bar'
    >
      {/* <NotificationBar /> */}
      {checkGracePeriodPayment.length > 0 && (
        <GracePeriodBanner
          checkGracePeriodPayment={checkGracePeriodPayment}
          className={'web'}
        />
      )}
      <Toolbar
        sx={{
          boxSizing: 'border-box',
          minHeight: { xs: 56, sm: 70 },
          paddingLeft: { xs: 5 },
          paddingRight: { xs: 5, md: 7.5 },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <IconButton
            sx={{ color: 'text.secondary' }}
            edge='start'
            className='menu-btn'
            color='inherit'
            aria-label='open drawer'
            onClick={() => setCollapsed(!isCollapsed)}
            size='large'
          >
            <MenuIcon
              sx={{
                width: 35,
                height: 35,
              }}
            />
          </IconButton>
        </Box>
        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
          <IconButton
            sx={{ color: 'text.secondary' }}
            edge='start'
            className='menu-btn'
            color='inherit'
            aria-label='open drawer'
            onClick={toggleNavCollapsed}
            size='large'
          >
            <MenuIcon
              sx={{
                width: 35,
                height: 35,
              }}
            />
          </IconButton>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
          }}
        />

        <Box
          sx={{
            // ml: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* <AppTooltip> */}
          <Box
            sx={{
              // ml: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {renderRoleSwitcher()}
              {isAdminRole(user) && (
                <Box
                  sx={{
                    px: 1.85,
                  }}
                >
                  <AppTooltip title='Help' placement={tooltipPosition}>
                    <IconButton
                      className='icon-btn'
                      sx={{
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        color: (theme) => theme.palette.text.secondary,
                        backgroundColor: (theme) =>
                          theme.palette.background.default,
                        // border: 1,
                        borderColor: 'transparent',
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover, &:focus': {
                          color: (theme) => theme.palette.text.primary,
                          backgroundColor: (theme) =>
                            alpha(theme.palette.background.default, 0.9),
                          borderColor: (theme) =>
                            alpha(theme.palette.text.secondary, 0.25),
                          transform: 'scale(1.1)',
                        },
                      }}
                      onClick={() =>
                        window.open(
                          'https://membertraxx.help.center',
                          '_blank',
                          'noopener,noreferrer',
                        )
                      }
                      size='large'
                      aria-label='Open Help Center'
                    >
                      <HelpRoundedIcon />
                    </IconButton>
                  </AppTooltip>
                </Box>
              )}

              {(isParentRole(user) || isStudentRole(user)) && (
                <Box
                  sx={{
                    px: 1.85,
                  }}
                >
                  <AppTooltip
                    title={
                      showDashboard
                        ? 'Switch to previous dashboard'
                        : 'Switch to new dashboard'
                    }
                    placement={tooltipPosition}
                  >
                    <IconButton
                      className='icon-btn'
                      sx={{
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        color: (theme) => theme.palette.text.secondary,
                        backgroundColor: (theme) =>
                          theme.palette.background.default,
                        border: 1,
                        borderColor: 'transparent',
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover, &:focus': {
                          color: (theme) => theme.palette.text.primary,
                          backgroundColor: (theme) =>
                            alpha(theme.palette.background.default, 0.9),
                          borderColor: (theme) =>
                            alpha(theme.palette.text.secondary, 0.25),
                          transform: 'scale(1.1)',
                        },
                      }}
                      size='large'
                    >
                      <Switch
                        checked={showDashboard}
                        onChange={(e) => setShowDashboard(e.target.checked)}
                        inputProps={{ 'aria-label': 'toggle dashboard' }}
                        size='small'
                      />
                    </IconButton>
                  </AppTooltip>

                  <AppTooltip title='Cart' placement={tooltipPosition}>
                    <IconButton
                      className='icon-btn'
                      sx={{
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        color: (theme) => theme.palette.text.secondary,
                        backgroundColor: (theme) =>
                          theme.palette.background.default,
                        border: 1,
                        borderColor: 'transparent',
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover, &:focus': {
                          color: (theme) => theme.palette.text.primary,
                          backgroundColor: (theme) =>
                            alpha(theme.palette.background.default, 0.9),
                          borderColor: (theme) =>
                            alpha(theme.palette.text.secondary, 0.25),
                          transform: 'scale(1.1)',
                        },
                      }}
                      onClick={() => navigate('/cart')}
                      size='large'
                    >
                      <ShoppingCartOutlinedIcon />
                      <Badge
                        badgeContent={cartData?.length}
                        color='error'
                        sx={{
                          right: -3,
                          top: -16,
                          transition: 'transform 0.3s ease-in-out',
                          transform: 'scale(1)',
                        }}
                      ></Badge>
                    </IconButton>
                  </AppTooltip>
                </Box>
              )}

              <AppNotifications
                notificationData={notificationData}
                fetchData={notifications}
                markAllRead={markAllRead}
              />
              <UserInfo onClick={(e) => e.stopPropagation()} />
            </Box>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
export default AppHeader;

AppHeader.propTypes = {
  toggleNavCollapsed: PropTypes.func,
  fetchDataFunction: PropTypes.func,
  isCollapsed: PropTypes.bool,
  setCollapsed: PropTypes.func,
  tooltipPosition: PropTypes.string,
  commonRoutes: PropTypes.array,
};
